import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../contexts/settings-context";
import {
  DOCUMENT_BLOCK_TYPES,
  resolveOfferCustomerName,
  resolveOfferDocumentTitle,
  resolveOfferIdRevSubtitle,
  resolveOfferRevisionLabel,
  stripHtmlToText,
} from "./offer-document-structure-defaults";
import { resolveOfferSalesAmount } from "./utils";
import { TWclassNames } from "../../utils/Div";
import { PromangRichTextContent } from "../../utils/PromangRichTextContent";
import OfferPrintLinesPreview from "./offer-print-lines-preview";
import "./offer-workspace.css";
import "../../utils/PromangRichTextEditor.css";

/**
 * Live A4-like paper preview — Claude: centered 640px paper, clear section hierarchy.
 */
export default function OfferDocumentPreview({
  offer,
  blocks = [],
  selectedBlockId,
}) {
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!selectedBlockId) return;
    const el = sectionRefs.current[selectedBlockId];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedBlockId]);

  const title = resolveOfferDocumentTitle(offer);
  const subtitle = resolveOfferIdRevSubtitle(offer, t);
  const salesAmount = resolveOfferSalesAmount(offer);
  const formattedPrice = priceFormatter
    ? priceFormatter.format(salesAmount)
    : String(salesAmount ?? 0);

  const enabledBlocks = blocks.filter((b) => b.enabled !== false);
  const hasCoverBlock = enabledBlocks.some(
    (block) => block.type === DOCUMENT_BLOCK_TYPES.COVER
  );

  return (
    <div className="flex h-full min-h-0 flex-1 justify-center overflow-y-auto bg-slate-100/60 px-4 py-4">
      <article
        className="offer-document-preview w-full max-w-[640px] shrink-0 bg-white px-6 py-6"
        style={{
          minHeight: 800,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(15,23,42,.06)",
        }}
        aria-label={t("offers.documentBuilder.preview.label")}
      >
        {!hasCoverBlock ? (
          <header className="offer-doc-global-header">
            <h1 className="offer-doc-global-title">
              {(() => {
                const resolved = title || t("offers.documentBuilder.preview.untitled");
                const normalized = String(resolved).trim().toLowerCase();
                const duplicate =
                  normalized === "tilbud" || normalized.startsWith("tilbud");
                return duplicate
                  ? resolved
                  : `${t("offers.documentBuilder.preview.title_prefix")} ${resolved}`;
              })()}
            </h1>
            {subtitle ? <p className="offer-doc-global-meta">{subtitle}</p> : null}
            <div className="offer-doc-global-divider" />
          </header>
        ) : null}

        <div>
          {enabledBlocks.map((block, index) => {
            const selected = block.id === selectedBlockId;
            const showPageBreakHint = block.page_break_before === true && index > 0;
            return (
              <React.Fragment key={block.id}>
                {showPageBreakHint ? (
                  <div className="offer-doc-page-break" aria-hidden="true">
                    <span className="offer-doc-page-break-line" />
                    {t("offers.documentBuilder.preview.page_break")}
                    <span className="offer-doc-page-break-line" />
                  </div>
                ) : null}
                <section
                  ref={(el) => {
                    sectionRefs.current[block.id] = el;
                  }}
                  className={TWclassNames(
                    "offer-doc-section rounded-md transition-colors",
                    showPageBreakHint && "offer-doc-section--new-page",
                    index > 0 && !showPageBreakHint && "mt-2",
                    selected && "bg-[#EAF3FD]/50 ring-1 ring-[#BFDCF7]"
                  )}
                  data-block-type={block.type}
                >
                  <BlockPreviewBody
                    block={block}
                    offer={offer}
                    formattedPrice={formattedPrice}
                    t={t}
                  />
                </section>
              </React.Fragment>
            );
          })}
        </div>
      </article>
    </div>
  );
}

function BlockPreviewBody({ block, offer, formattedPrice, t }) {
  const type = block.type;
  const rawBody = block.content?.body || "";
  const bodyText = stripHtmlToText(rawBody);

  if (type === DOCUMENT_BLOCK_TYPES.COVER) {
    const coverTitle =
      offer?.cover_page_title || resolveOfferDocumentTitle(offer);
    const coverDesc = offer?.cover_page_description || "";
    const customer = resolveOfferCustomerName(offer);
    const rev = resolveOfferRevisionLabel(offer);

    return (
      <div className="offer-doc-cover">
        {block.title ? <p className="offer-doc-kicker">{block.title}</p> : null}
        <p className="offer-doc-hero-title">
          {coverTitle || t("offers.documentBuilder.preview.untitled")}
        </p>
        {customer ? <p className="offer-doc-cover-meta">{customer}</p> : null}
        {rev != null ? (
          <p className="offer-doc-cover-meta offer-doc-cover-meta--muted">
            {t("offers.documentBuilder.preview.rev", { rev })}
          </p>
        ) : null}
        <PromangRichTextContent
          html={coverDesc}
          className="offer-doc-body text-[12.5px] leading-relaxed text-slate-700"
          empty={
            <p className="offer-doc-empty">{t("offers.documentBuilder.preview.empty_section")}</p>
          }
        />
      </div>
    );
  }

  if (type === DOCUMENT_BLOCK_TYPES.PRICE) {
    const currency = offer?.currency || "NOK";
    const note = block.content?.note || "";
    return (
      <div>
        <SectionHeading title={block.title} />
        <p className="mb-3 text-[12.5px] text-slate-700">
          {t("offers.documentBuilder.preview.price_label")}:{" "}
          <span className="font-medium tabular-nums">{formattedPrice}</span>
        </p>
        <OfferPrintLinesPreview
          items={(offer?.items || []).filter(
            (item) =>
              item?.visible_to_customer !== false &&
              !["optional", "excluded"].includes(item?.pricing_role)
          )}
          currency={currency}
          compact
          showFooterTotal
          className="overflow-hidden border border-slate-200 bg-white"
        />
        <PromangRichTextContent
          html={note}
          className="mt-3 text-[12px] italic text-slate-500"
        />
      </div>
    );
  }

  if (type === DOCUMENT_BLOCK_TYPES.ALTERNATIVES) {
    const currency = offer?.currency || "NOK";
    const visible = (offer?.items || []).filter(
      (item) =>
        item?.visible_to_customer !== false && item?.pricing_role !== "excluded"
    );
    const groups = new Map();
    const ungrouped = [];
    visible.forEach((item) => {
      const group = String(item?.alternative_group || "").trim();
      if (group) {
        if (!groups.has(group)) groups.set(group, []);
        groups.get(group).push(item);
      } else if (item?.pricing_role === "optional") {
        ungrouped.push(item);
      }
    });
    const groupEntries = [...groups.entries()].filter(
      ([, rows]) =>
        rows.length > 1 || rows.some((row) => row?.pricing_role === "optional")
    );
    const hasAlternatives = groupEntries.length > 0 || ungrouped.length > 0;

    return (
      <div>
        <SectionHeading title={block.title} />
        {bodyText ? (
          <PromangRichTextContent
            html={rawBody}
            className="mb-2 text-[12.5px] leading-relaxed text-slate-700"
          />
        ) : null}
        {hasAlternatives ? (
          <div className="space-y-3">
            <p className="text-[11px] text-slate-500">
              {t("offers.documentBuilder.preview.alternatives_hint")}
            </p>
            {groupEntries.map(([groupName, rows]) => (
              <div key={groupName}>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {groupName}
                </p>
                <ul className="space-y-1">
                  {rows.map((item) => (
                    <li
                      key={item.id || `${groupName}-${item.title}`}
                      className="flex items-baseline justify-between gap-3 text-[12.5px] text-slate-700"
                    >
                      <span>
                        {item.title || item.description || "—"}
                        <span className="ml-2 text-[11px] text-slate-400">
                          {item.pricing_role === "included"
                            ? t("offers.documentBuilder.preview.alternative_selected")
                            : t("offers.documentBuilder.preview.alternative_option")}
                        </span>
                      </span>
                      <span className="tabular-nums text-slate-600">
                        {formatPreviewMoney(item, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {ungrouped.length > 0 ? (
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {t("offers.documentBuilder.preview.optional_extras")}
                </p>
                <ul className="space-y-1">
                  {ungrouped.map((item) => (
                    <li
                      key={item.id || item.title}
                      className="flex items-baseline justify-between gap-3 text-[12.5px] text-slate-700"
                    >
                      <span>{item.title || item.description || "—"}</span>
                      <span className="tabular-nums text-slate-600">
                        {formatPreviewMoney(item, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-[12.5px] italic text-slate-400">
            {bodyText || t("offers.documentBuilder.preview.empty_section")}
          </p>
        )}
      </div>
    );
  }

  if (type === DOCUMENT_BLOCK_TYPES.ATTACHMENTS) {
    return (
      <div>
        <SectionHeading title={block.title} />
        {bodyText ? (
          <PromangRichTextContent
            html={rawBody}
            className="text-[12.5px] italic text-slate-500"
          />
        ) : (
          <p className="text-[12.5px] italic text-slate-500">
            {t("offers.documentBuilder.preview.attachments_placeholder")}
          </p>
        )}
      </div>
    );
  }

  // Fallback for empty text blocks: offer description/comment (MVP seed).
  const fallbackHtml =
    !bodyText &&
    (type === DOCUMENT_BLOCK_TYPES.LETTER ||
      type === DOCUMENT_BLOCK_TYPES.PROJECT_DESCRIPTION ||
      type === DOCUMENT_BLOCK_TYPES.SCOPE)
      ? offer?.description || offer?.comment || ""
      : "";
  const displayHtml = bodyText ? rawBody : fallbackHtml;

  return (
    <div>
      <SectionHeading title={block.title} />
      <PromangRichTextContent
        html={displayHtml}
        className="text-[12.5px] leading-relaxed text-slate-700"
        empty={
          <p className="text-[12.5px] italic text-slate-400">
            {t("offers.documentBuilder.preview.empty_section")}
          </p>
        }
      />
    </div>
  );
}

function SectionHeading({ title }) {
  if (!title) return null;
  return <h2 className="offer-doc-heading">{title}</h2>;
}

function formatPreviewMoney(item, currency) {
  const qty = Number(item?.quantity) || 0;
  const price = Number(item?.sales_price) || 0;
  const amount = qty * price;
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: currency || "NOK",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (_err) {
    return `${amount} ${currency || "NOK"}`;
  }
}
